import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { ImpactScoresByIVV } from '@upright/common/model/output/scores/types';
import { dimensionBarsLayout } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/dimension-bars-layout';
import { impactBarsLayout } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/impact-bars-layout';
import { CarouselLayout } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { LensData, LensOptions } from '@upright/ui/common/upright/core/impact-lens-profile/types';
import { ImpactInsight } from '@upright/ui/common/upright/core/impact-lenses/insights/common/types';
import { NetImpactLensSelectionState } from '@upright/ui/common/upright/core/impact-lenses/net-impact/selection-state';
import {
  NetImpactLensData,
  NetImpactLensOptions,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/types';
import { ImpactLens } from '@upright/ui/common/upright/core/impact-lenses/types';
import { Context, createContext, useContext } from 'react';
import { LensSelectionStates } from '../impact-lenses/selection-hook';

export interface CarouselContext<
  A extends ImpactLensType = ImpactLensType,
  B extends LensData<A> = LensData<A>,
  C extends LensOptions<A> = LensOptions<A>,
  D extends ImpactLens<A, B, C> = ImpactLens<A, B, C>,
> {
  width: number;
  layout: CarouselLayout;
  data: LensData<A>;

  modalImpactCode: string | undefined;
  setModalImpactCode: (code: string | undefined) => void;

  selectionState: LensSelectionStates[A];
  // Causes problems: because of this type, CarouselContext<specificLens> is not assignable to CarouselContext<anyLens>.
  // This is because input type of this function becomes wider, meaning more specific function.
  // Basically, setting lens-specific state is not compatible with idea of shared components doing it.
  setSelectionState: (newState: LensSelectionStates[A]) => void;

  options: LensOptions<A>;

  lens: D;
  lensType: A;
  lensIndex: number;
  previousLensIndex: number | undefined;

  desktopMode: boolean;

  // TODO
  negativeColor: string;
  positiveColor: string;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
export const carouselContext = createContext<CarouselContext<ImpactLensType>>({} as any);

// TODO ML: Could add lens as optional parameter here, to handle type cast always correctly
export function useCarouselContext<A extends ImpactLensType = ImpactLensType>() {
  const context = useContext<CarouselContext<A>>(carouselContext as unknown as Context<CarouselContext<A>>);
  return context;
}

export function useCarouselLayout() {
  const context = useCarouselContext();
  return context.layout;
}

export function useImpactBarsLayout() {
  const context = useCarouselContext();
  function getDimensionBarsLayout() {
    if (context.lensType === ImpactLensType.UPRIGHT_NET_IMPACT) {
      const state = context.selectionState as NetImpactLensSelectionState;
      const showDimensions = state.showDimensionBars;
      const niLensData = context.data as NetImpactLensData | undefined;
      const options = context.options as NetImpactLensOptions;
      if (!showDimensions || !niLensData || !niLensData.scores) {
        return undefined;
      }
      return dimensionBarsLayout(context.layout, niLensData.scores, options.scoreUnit, niLensData.ghostScores);
    }
    return undefined;
  }
  const data = context.data as {
    scores?: ImpactScoresByIVV;
    insights?: ImpactInsight[];
    ghostScores?: ImpactScoresByIVV;
  };
  return impactBarsLayout(context.layout, data, getDimensionBarsLayout());
}
